import { useMutation } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { Ban, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctCaseCancellationReason } from '@/generated/gql/graphql';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';

import { NEGOTIATION_DEMAND_CANCEL_MUTATION } from './NegotiationDemandCancel.gql';

const REASON_OPTIONS: TsrctCaseCancellationReason[] = [
  'NO_LONGER_NEEDED' as TsrctCaseCancellationReason,
  'BUDGET_CONSTRAINT' as TsrctCaseCancellationReason,
  'REQUIREMENTS_CHANGED' as TsrctCaseCancellationReason,
  'AWARDED_OUTSIDE_PLATFORM' as TsrctCaseCancellationReason,
  'OTHER' as TsrctCaseCancellationReason,
];
const NO_REASON_VALUE = '__NONE__';

interface FormData {
  reason: TsrctCaseCancellationReason | null;
  message: string | null;
}

export interface NegotiationDemandCancelDialogProps {
  demandId: string;
  open: boolean;
  onClose: () => void;
  onCancelled?: () => void;
}

export const NegotiationDemandCancelDialog = ({
  demandId,
  open,
  onClose,
  onCancelled,
}: NegotiationDemandCancelDialogProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'NegotiationDemandDetail.action.cancel.dialog' });
  const { t: tCommon } = useTranslation('common');
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const [formData, setFormData] = useState<FormData>({ reason: null, message: null });

  const [cancelDemand, { loading }] = useMutation(NEGOTIATION_DEMAND_CANCEL_MUTATION, {
    onError,
  });

  const handleApply = async () => {
    const { errors } = await cancelDemand({
      variables: {
        id: demandId,
        data: {
          reason: formData.reason,
          message: formData.message,
        },
      },
    });

    if (errors) return;

    enqueueSuccessSnackbar(t('success'));
    onCancelled?.();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent showCloseButton={false} className="w-[95vw] max-w-2xl p-6">
        <DialogHeader className="space-y-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="bg-muted text-muted-foreground inline-flex h-8 w-8 items-center justify-center rounded-full">
              <Ban className="h-4 w-4" />
            </span>
            {t('title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="negotiation-demand-cancel-reason">{t('reason.label')}</Label>
            <Select
              value={formData.reason ?? NO_REASON_VALUE}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  reason: value === NO_REASON_VALUE ? null : (value as TsrctCaseCancellationReason),
                }))
              }
            >
              <SelectTrigger id="negotiation-demand-cancel-reason" className="w-full" disabled={loading}>
                <SelectValue placeholder={t('reason.none')}>
                  {formData.reason ? tCommon(`CaseCancellationReason.${formData.reason}`) : t('reason.none')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                <SelectItem value={NO_REASON_VALUE}>{t('reason.none')}</SelectItem>
                {REASON_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {tCommon(`CaseCancellationReason.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="negotiation-demand-cancel-message">{t('message.label')}</Label>
            <Textarea
              id="negotiation-demand-cancel-message"
              rows={8}
              disabled={loading}
              value={formData.message ?? ''}
              onChange={(event) => setFormData((prev) => ({ ...prev, message: event.target.value }))}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('cancelButton')}
          </Button>
          <Button onClick={handleApply} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('applyButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
